function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getWallLength(wall) {
  const sx = toNumber(wall?.start?.x);
  const sz = toNumber(wall?.start?.z);
  const ex = toNumber(wall?.end?.x);
  const ez = toNumber(wall?.end?.z);

  const dx = ex - sx;
  const dz = ez - sz;

  return Math.sqrt(dx * dx + dz * dz);
}

function getWallsFromIntent(intent = {}) {
  if (Array.isArray(intent?.room?.walls) && intent.room.walls.length) {
    return intent.room.walls;
  }

  if (Array.isArray(intent?.walls) && intent.walls.length) {
    return intent.walls;
  }

  const width = toNumber(intent?.room?.width || intent?.room?.wallA, 3000);
  const depth = toNumber(intent?.room?.depth || intent?.room?.wallB, 0);
  const height = toNumber(intent?.room?.height, 2800);

  const walls = [
    {
      id: "back_wall",
      name: "Back wall",
      start: { x: 0, z: 0 },
      end: { x: width, z: 0 },
      height,
      inward: "positive_z",
    },
  ];

  if (depth > 0 || intent.layoutType === "l_shape" || intent.layoutType === "u_shape") {
    walls.push({
      id: "left_wall",
      name: "Left wall",
      start: { x: 0, z: 0 },
      end: { x: 0, z: depth || 2200 },
      height,
      inward: "positive_x",
    });
  }

  if (intent.layoutType === "u_shape") {
    walls.push({
      id: "right_wall",
      name: "Right wall",
      start: { x: width, z: 0 },
      end: { x: width, z: depth || 2200 },
      height,
      inward: "negative_x",
    });
  }

  return walls;
}

function getRunWidth(run = {}) {
  return (run.cabinets || []).reduce(
    (sum, cabinet) => sum + toNumber(cabinet.width, 0),
    0
  );
}

export function validateKitchenIntent(intent = {}) {
  const errors = [];
  const warnings = [];

  const walls = getWallsFromIntent(intent);
  const roomHeight = toNumber(intent?.room?.height, 2800);
const ceilingClearance = toNumber(intent?.room?.ceilingClearance, 50);
const maxCabinetTop = roomHeight - ceilingClearance;

  const wallMap = new Map(
    walls.map((wall) => [String(wall.id), wall])
  );

  const runs = Array.isArray(intent.runs) ? intent.runs : [];

  if (!runs.length) {
    errors.push({
      message: "Kitchen intent has no runs.",
    });
  }

  runs.forEach((run, runIndex) => {
    if (!run.id) {
      warnings.push({
        runIndex,
        message: "Run has no id.",
      });
    }

    if (!["base", "wall", "tall"].includes(String(run.type))) {
      errors.push({
        runIndex,
        message: `Invalid run type "${run.type}".`,
      });
    }

    if (run.wallId) {
      const wall = wallMap.get(String(run.wallId));

      if (!wall) {
        errors.push({
          runIndex,
          message: `Run "${run.id}" references missing wall "${run.wallId}".`,
        });
        return;
      }

      const wallLength = getWallLength(wall);
      const offset = toNumber(run.offsetFromWallStart, 0);
      const runWidth = getRunWidth(run);

      if (offset < 0) {
        errors.push({
          runIndex,
          message: `Run "${run.id}" has negative offset.`,
        });
      }

      if (offset + runWidth > wallLength + 0.001) {
        errors.push({
          runIndex,
          message: `Run "${run.id}" does not fit on wall "${run.wallId}". Required ${offset + runWidth}mm, available ${Math.round(wallLength)}mm.`,
        });
      }
    } else {
      warnings.push({
        runIndex,
        message: `Run "${run.id || runIndex}" is not wall-based. Prefer wallId + offsetFromWallStart.`,
      });
    }

    if (run.type === "wall") {
      const floorOffset = toNumber(run.floorOffset, 0);

      if (floorOffset < 1000) {
        warnings.push({
          runIndex,
          message: `Wall cabinet run "${run.id}" has low floorOffset ${floorOffset}mm.`,
        });
      }
    }

    const defaultHeightByRunType =
  run.type === "wall" ? 700 :
  run.type === "tall" ? 2100 :
  720;

const runHeight = toNumber(
  run.defaultHeight,
  defaultHeightByRunType
);

const runFloorOffset = toNumber(
  run.floorOffset,
  run.type === "wall" ? 1500 : 150
);

const runTopY = runFloorOffset + runHeight;

if (runTopY > roomHeight) {
  errors.push({
    runIndex,
    message: `Run "${run.id}" exceeds room height. Top is ${runTopY}mm, room height is ${roomHeight}mm.`,
  });
}

if (runTopY > maxCabinetTop) {
  errors.push({
    runIndex,
    message: `Run "${run.id}" exceeds allowed top height. Top is ${runTopY}mm, max allowed is ${maxCabinetTop}mm because ceiling clearance is ${ceilingClearance}mm.`,
  });
}

   (run.cabinets || []).forEach((cabinet, cabinetIndex) => {
  const width = toNumber(cabinet.width, 0);

  if (width <= 0) {
    errors.push({
      runIndex,
      cabinetIndex,
      message: `Cabinet has invalid width.`,
    });
  }

  if (width > 1500) {
    warnings.push({
      runIndex,
      cabinetIndex,
      message: `Cabinet width ${width}mm is unusually large.`,
    });
  }

  const defaultHeightByType =
    run.type === "wall" ? 700 :
    run.type === "tall" ? 2100 :
    720;

  const defaultDepthByType =
    run.type === "wall" ? 320 :
    560;

  const cabinetHeight = toNumber(
    cabinet.height,
    toNumber(run.defaultHeight, defaultHeightByType)
  );

  const cabinetFloorOffset = toNumber(
    cabinet.floorOffset,
    toNumber(run.floorOffset, run.type === "wall" ? 1500 : 150)
  );

  const topY = cabinetFloorOffset + cabinetHeight;

  const cabinetDepth = toNumber(
    cabinet.depth,
    toNumber(run.defaultDepth, defaultDepthByType)
  );

  if (run.type === "wall" && cabinetDepth > 450) {
    warnings.push({
      runIndex,
      cabinetIndex,
      message: `Wall cabinet depth ${cabinetDepth}mm is unusually large.`,
    });
  }
});
  });

  return {
    ok: errors.length === 0,
    errors,
    warnings,
  };
}